import { assocPath, groupBy, map, pipe, reduce } from 'ramda'

const parseKeyDefault = (key: string): Array<string> =>
  key.replace(/\[([^\]]+)\]/g, '.$1').split('.')

interface NestedRecord {
  [key: string]: string | Array<string> | NestedRecord
}

/**
 * Парсинг FormData в объект с поддержкой вложенных полей
 * и динамических групп полей (добавление по кнопке)
 * и полей с одним значением name (группы чекбоксов или радио)
 * По умолчанию поддерживает имена вида fieldNameRoot[id].fieldName.FieldName...,
 * но можно переопределить через parseKey
 */

export const parseFormData = <T = NestedRecord>(
  formData: FormData,
  parseKey: (key: string) => Array<string> = parseKeyDefault,
): T => {
  const formEntries = Array.from(formData.entries()) as [string, string][]

  const result = pipe(
    // Шаг A: Группируем по ключу. Получаем: { "permissions": ["read", "write"], "user.name": ["Иван"] }
    (entries: [string, string][]) => groupBy((entry) => entry[0], entries) as any,

    // Шаг B: Превращаем массивы значений в одно значение или в массив, если их несколько
    map((entries: [string, string][]) => {
      const values = entries.map((entry) => entry[1])
      return values.length > 1 ? values : values[0]
    }),

    // Шаг C: Разворачиваем плоский объект в структуру дерева через assocPath
    (groupedObj: Record<string, any>) =>
      reduce(
        (acc, [key, value]) => assocPath(parseKey(key), value, acc),
        {} as Record<string, any>,
        Object.entries(groupedObj),
      ),
  )(formEntries)

  return result as T
}
